import {
  Box,
  CircularProgress,
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon,
  Stack,
} from '@mui/material';
import { PageWithDrawer, useOnMount } from '@prismamedia/one-components';
import type { FC } from 'react';
import {
  createRef,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
} from 'react';
import FlipMove from 'react-flip-move';
import { useHistory, useParams } from 'react-router-dom';
import { usePrevious, useUnmount } from 'react-use';
import { Skeletons } from '../../../../components/Skeletons';
import { useQuery } from '@apollo/client';
import {
  GetCategoryForEditQuery,
  GetCategoryForEditQueryVariables,
} from '../../../../__generated__/queries-web';
import { GET_CATEGORY_FOR_EDIT } from '../../apollo/queries/categories.web.graphql';
import { CategoriesContext } from '../../categories.context';
import { FormContext } from '../../form.context';
import { getCategoryForm } from '../../models/categoryForm.model';
import { Category } from '../../types';
import {
  getActivePathCategoryId,
  isPageCreateView,
  isPageEditView,
  navigateTo,
} from '../../utils';
import { DrawerContent } from './components/DrawerContent';
import { GeneralConfiguration } from './components/GeneralConfiguration';
import { Section } from './components/Section';
import { useEditSection } from './components/Section/useEditSection';
import {
  ALLOWED_CATEGORY_FORM_SECTIONS,
  GENERAL_CONFIG_ANCHOR_HASH,
  LIST_ANIMATION_DELAY,
  LIST_ANIMATION_DURATION,
  LIST_ENTER_ANIMATION,
  SECTION_PLACEHOLDER_HEIGHT,
} from './constants';
import { useStyles } from './styles';

let AUTO_SCROLL = false;

export const Edit: FC = () => {
  const {
    activeCategory,
    brandKey,
    isCategoriesLoading,
    categories,
    setActiveCategory,
  } = useContext(CategoriesContext);
  const { form, handlers: formHandlers } = useContext(FormContext);

  const { handlers: sectionHandlers } = useEditSection();

  const classes = useStyles();
  const drawerCenterRef = useRef(null);
  const history = useHistory();
  const prevFormSectionsLength = usePrevious(form.sections.length);
  const routeParams = useParams();

  // Get the category ID from the URL for detailed editing
  const activeCategoryPathname = getActivePathCategoryId(routeParams);

  // Use detailed edit query only when editing a specific category
  const {
    data: detailedCategoryData,
    loading: isDetailedCategoryLoading,
  } = useQuery<GetCategoryForEditQuery, GetCategoryForEditQueryVariables>(
    GET_CATEGORY_FOR_EDIT,
    {
      variables: {
        where: {
          id: activeCategoryPathname || '',
        },
      },
      skip: !activeCategoryPathname || !isPageEditView() || !brandKey,
    },
  );

  const handleSetForm = useCallback(
    (category: Category) => {
      // Get parentId from URL path parameter (lvl1) if category.id is not available
      const parentId = routeParams.lvl1;

      const newForm =
        isPageEditView() && category
          ? getCategoryForm(category)
          : getCategoryForm({
              brandKey,
              format: category.format,
              level: category.level + 1,
              medias: `[]`,
              parent: {
                id: category.id || parentId || '',
                title: category.title,
              },
            });
      formHandlers.setForm(newForm);
    },
    [formHandlers, brandKey, routeParams.lvl1],
  );

  const handleCreateForm = useCallback(() => {
    // For create mode, get parent ID from URL path parameter
    const parentId = routeParams.lvl1;
    const format = routeParams.format as any;

    const newForm = getCategoryForm({
      brandKey,
      format,
      level: 3, // Default level for new categories
      medias: `[]`,
      parent: {
        id: parentId || '',
        title: '', // We don't have the parent title in create mode
      },
    });

    formHandlers.setForm(newForm);
  }, [formHandlers, brandKey, routeParams.lvl1, routeParams.format]);

  /**
   * Fill the form as empty or as current active category
   * depending on the path (/create and /edit)
   */
  useEffect(() => {
    if (isPageCreateView()) {
      // When creating a new category, use parent ID from URL
      handleCreateForm();
    } else if (activeCategoryPathname) {
      // When editing a specific category, use detailed data if available
      if (detailedCategoryData?.category) {
        const categoryData = detailedCategoryData.category as Category;
        handleSetForm(categoryData);
        // Update the activeCategory in context for proper back navigation
        setActiveCategory(categoryData);
      } else if (!isDetailedCategoryLoading) {
        // Fallback to list data if detailed data is not available
        const categoryToEdit = categories.find(
          (cat) => cat.id === activeCategoryPathname,
        );
        if (categoryToEdit) {
          const categoryData = (categoryToEdit as unknown) as Category;
          handleSetForm(categoryData);
          // Update the activeCategory in context for proper back navigation
          setActiveCategory(categoryData);
        }
      }
    }
    // Don't run this effect if we don't have the necessary data
  }, [
    activeCategoryPathname,
    detailedCategoryData,
    isDetailedCategoryLoading,
    categories,
    activeCategory,
    handleSetForm,
    handleCreateForm,
    setActiveCategory,
  ]);

  /**
   * Put the default hash in path when it doesn't exists
   */
  useOnMount(() => {
    if (!history.location.hash) {
      navigateTo({ opts: { hash: GENERAL_CONFIG_ANCHOR_HASH } });
    }
  });

  /**
   * Jump to the active section DOM node when the view component mounts
   */
  useLayoutEffect(() => {
    const { hash } = history.location;
    const isAllowedToScroll = Boolean(
      !isCategoriesLoading &&
        AUTO_SCROLL &&
        form.sections.length &&
        hash !== GENERAL_CONFIG_ANCHOR_HASH &&
        hash,
    );

    if (isAllowedToScroll && drawerCenterRef?.current) {
      AUTO_SCROLL = false;
      // We jump until end of drawerCenter element
      const DrawerCenterNode: HTMLDivElement = drawerCenterRef.current;
      // eslint-disable-next-line immutable/no-mutation
      DrawerCenterNode.scrollTop = DrawerCenterNode.scrollHeight;
    }
  }, [form.sections.length, history.location, isCategoriesLoading]);

  /**
   * Reset the form when the view component unmounts
   */
  useUnmount(() => {
    formHandlers.resetForm();
  });

  // Show loader when fetching detailed category data in edit mode
  if (isDetailedCategoryLoading && isPageEditView()) {
    return (
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
        }}
      >
        <CircularProgress size={80} />
      </Box>
    );
  }

  return (
    <Box className={classes.wrapper} component="div">
      <PageWithDrawer
        classes={{ leftDrawer: classes.leftDrawer }}
        leftDrawer={<DrawerContent />}
        ref={drawerCenterRef}
      >
        {/* GENERAL CONFIGURATION */}
        <GeneralConfiguration />

        {/* SECTIONS CONFIGURATION */}
        <Stack>
          {isCategoriesLoading && isPageEditView() ? (
            <Skeletons
              height={SECTION_PLACEHOLDER_HEIGHT}
              quantity={2}
              spacing={3}
            />
          ) : (
            <FlipMove
              appearAnimation={true}
              delay={LIST_ANIMATION_DELAY}
              disableAllAnimations={
                !form.sections.length ||
                form.sections.length === prevFormSectionsLength
              }
              duration={LIST_ANIMATION_DURATION}
              enterAnimation={LIST_ENTER_ANIMATION}
              leaveAnimation="fade"
            >
              {form.sections.map((sectionProps) => {
                return (
                  <Section
                    key={sectionProps.id}
                    {...sectionProps}
                    ref={createRef()}
                  />
                );
              })}
            </FlipMove>
          )}
        </Stack>
      </PageWithDrawer>

      {/* ADD SECTION BUTTON */}
      {!isCategoriesLoading && (
        <SpeedDial
          ariaLabel="Page edit actions"
          className={classes.speedDial}
          direction="up"
          icon={<SpeedDialIcon />}
        >
          {ALLOWED_CATEGORY_FORM_SECTIONS.map((action) => (
            <SpeedDialAction
              key={action.label}
              icon={action.icon}
              tooltipTitle={action.label}
              onClick={() => {
                sectionHandlers.handleSectionAdd({
                  type: action.type,
                  cb: (newSection) => {
                    // We jump the scroll until new section node
                    AUTO_SCROLL = true;
                    const search = new URLSearchParams({
                      from: 'SpeedDial',
                    }).toString();
                    navigateTo({
                      opts: {
                        hash: newSection.id,
                        search,
                      },
                    });
                  },
                });
              }}
            />
          ))}
        </SpeedDial>
      )}
    </Box>
  );
};
